import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError, tokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootError, setBootError] = useState(null);

  const reload = useCallback(async () => {
    if (!tokens.access()) return setUser(false);
    setBootError(null);
    try {
      const { data } = await api.get("/auth/me", { timeout: 10000 });
      setUser(data);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        tokens.clear();
        setUser(false);
      } else {
        setBootError(apiError(e));
      }
    }
  }, []);

  useEffect(() => {
    reload();
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [reload]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokens.set(data.access_token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* stateless */ }
    tokens.clear();
    setUser(false);
  };

  const can = useCallback((module, action = "view") => {
    if (!user) return false;
    if (module === "takip" && user.takip_override != null) return user.takip_override && action === "view";
    return user.is_super || (user.permissions?.[module] || []).includes(action);
  }, [user]);
  const flag = useCallback((name) => !!user && (user.is_super || !!user.flags?.[name]), [user]);

  return <AuthContext.Provider value={{ user, bootError, login, logout, reload, can, flag }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
